import { useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Calendar,
  Link as LinkIcon,
} from "lucide-react";
import { api } from "@/lib/api";

export function EmailTestPanel() {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const send = async () => {
    if (!to.trim()) { toast.error("Enter a recipient"); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/test-email", { to: to.trim() });
      setResult(data);
      if (data.sent) toast.success("Sent — check the inbox (and Resend dashboard)");
      else toast.error("Resend reported failure — see panel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <details className="mb-4 rounded-xl border border-gg-border bg-gg-surface">
      <summary className="cursor-pointer px-4 py-2 text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold select-none">
        Email diagnostics — verify Resend delivery
      </summary>
      <div className="px-4 pb-4 pt-2 flex flex-wrap items-end gap-2">
        <label className="block flex-1 min-w-[240px]">
          <span className="text-[10px] uppercase tracking-[0.14em] text-gg-brown font-semibold">Send test email to</span>
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="e.g. yourname@gmail.com"
            className="mt-1 w-full h-9 rounded-md border border-gg-border bg-gg-surface px-3 text-sm"
            data-testid="email-test-to"
          />
        </label>
        <button
          type="button"
          onClick={send}
          disabled={busy}
          className="btn-pill btn-primary text-xs disabled:opacity-60"
          data-testid="email-test-send"
        >
          {busy ? "Sending…" : "Send test"}
        </button>
        {result && (
          <p className="text-xs text-gg-muted w-full mt-1">
            {result.sent
              ? <>Accepted by Resend (msg id: <code>{result.resend_msg_id}</code>). If it doesn&apos;t arrive in your inbox/spam within 1 min, check the Resend dashboard delivery status — that tells you if it bounced or was blocked at the recipient mailserver.</>
              : <>Resend declined: {result.reason || "unknown"}.</>}
          </p>
        )}
      </div>
    </details>
  );
}

export function GCalBanner({ gcal, refetch }) {
  const [busy, setBusy] = useState(false);

  if (!gcal) return null;

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/google/oauth/start");
      window.location.href = data.authorization_url;
    } catch {
      toast.error("Could not start Google sign-in");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Google Calendar? New booking confirmations will no longer create calendar events.")) return;
    try {
      await api.post("/google/oauth/disconnect");
      toast.success("Google Calendar disconnected");
      refetch();
    } catch {
      toast.error("Could not disconnect");
    }
  };

  if (!gcal.configured) {
    return (
      <div className="mb-6 rounded-2xl border border-[#B24C4C]/40 bg-[#B24C4C]/8 p-5 flex items-start gap-4">
        <AlertTriangle className="text-[#B24C4C] shrink-0" />
        <div>
          <p className="font-semibold text-[var(--gg-text)]">Google Calendar not configured</p>
          <p className="text-sm text-gg-muted mt-1">
            The server is missing GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET. Bookings still work — they just won&apos;t create calendar events on confirmation.
          </p>
        </div>
      </div>
    );
  }

  if (gcal.connected) {
    return (
      <div className="mb-6 rounded-2xl border border-gg-green/40 bg-gg-green/8 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="gcal-status-connected">
        <div className="flex items-start gap-4">
          <CheckCircle2 className="text-gg-green shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-[var(--gg-text)]">Google Calendar connected</p>
            <p className="text-sm text-gg-muted mt-1">
              Confirming a booking will automatically create an event on <strong>{gcal.google_email}</strong>&apos;s primary calendar and invite the parent.
            </p>
          </div>
        </div>
        <button onClick={disconnect} className="btn-pill btn-outline text-sm" data-testid="gcal-disconnect-btn">
          <XCircle size={14} /> Disconnect
        </button>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-2xl border border-gg-brown/40 bg-gg-bg p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="gcal-status-disconnected">
      <div className="flex items-start gap-4">
        <Calendar className="text-gg-brown shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-[var(--gg-text)]">Connect your Google Calendar</p>
          <p className="text-sm text-gg-muted mt-1">
            Sign in once with your Google account so confirmed 1-on-1 bookings automatically land on your real calendar with the parent invited.
          </p>
        </div>
      </div>
      <button
        onClick={connect}
        disabled={busy}
        className="btn-pill btn-primary disabled:opacity-60"
        data-testid="gcal-connect-btn"
      >
        <LinkIcon size={14} /> {busy ? "Opening Google…" : "Connect Google Calendar"}
      </button>
    </div>
  );
}
